// Client-side persistence for Code Academy AI chat sessions.
//
// A per-user copy in localStorage (one JSON blob per Firebase uid) opens the
// panel with zero network. The server keeps every chat for 30 days as well;
// mergeServerSessions folds in what this device lacks, from another device or
// a cleared browser.
//
// Everything here is pure and defensive: reads validate shape (foreign/corrupt
// values fall back to an empty store) and writes are wrapped so a disabled or
// full localStorage never throws into the UI.

import { ChatScopeLesson, ChatServerMessage, ChatServerSession } from 'external/firebase/controllers/chat';

const OLD_KEY_PREFIX = 'kursuok_chat';
const NEW_KEY_PREFIX = 'cao_chat';
// A bare session id from before per-session persistence; ChatPanel drops it.
const LEGACY_SESSION_KEY = 'kursuok_chat_session_id';

// The key an earlier build's chat key now lives under, or null for any other key.
export const renamedChatKey = (key: string): string | null =>
    key.startsWith(OLD_KEY_PREFIX) && key !== LEGACY_SESSION_KEY
        ? `${NEW_KEY_PREFIX}${key.slice(OLD_KEY_PREFIX.length)}`
        : null;

let chatKeysRenamed = false;

// Once per page load, before the first read: moves every old chat key to its
// new name, keeping a value already stored under the new one.
export const ensureChatKeysRenamed = (): void => {
    if (chatKeysRenamed) {
        return;
    }
    chatKeysRenamed = true;
    try {
        const oldKeys = Object.keys(localStorage).filter(key => renamedChatKey(key) !== null);
        oldKeys.forEach(oldKey => {
            const newKey = renamedChatKey(oldKey) as string;
            const value = localStorage.getItem(oldKey);
            if (value !== null && localStorage.getItem(newKey) === null) {
                localStorage.setItem(newKey, value);
            }
            localStorage.removeItem(oldKey);
        });
    } catch {
        // ignore — storage disabled; there is nothing to move then either
    }
};

// Every key starts with "cao_chat". Earlier builds used "kursuok_chat" (the
// panel was modelled on kursuok.lt's); ensureChatKeysRenamed moves those over.
const KEY_PREFIX = 'cao_chat:';
// Cap stored sessions so a heavy user can't grow the blob past the ~5MB origin
// quota. Oldest (by updatedAt) are dropped first; the active one is always kept.
const MAX_SESSIONS = 20;
const TITLE_MAX = 40;

export interface StoredMessage {
    role: 'user' | 'assistant';
    content: string;
    // Set on replies stored by the content service — what a rating refers to.
    messageId?: number;
    rating?: 1 | -1;
}

export interface ChatSession {
    id: string; // client-generated session_id; also the React key. The backend
    // creates the matching conversation row lazily on the first real message.
    title: string; // '' until the first real user message
    createdAt: number;
    updatedAt: number;
    messages: StoredMessage[]; // greeting is messages[0], a canned local message
    // Set while the server holds replies this device doesn't; the transcript is
    // fetched when the chat is opened.
    serverMessageCount?: number;
}

export interface ChatStore {
    activeSessionId: string | null;
    sessions: ChatSession[];
}

// ----------------------------------------------------------------------

export const emptyStore = (): ChatStore => ({ activeSessionId: null, sessions: [] });

const storageKey = (uid: string): string => `${KEY_PREFIX}${uid}`;

const isMessage = (value: unknown): value is StoredMessage => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const message = value as Record<string, unknown>;
    return (message.role === 'user' || message.role === 'assistant') && typeof message.content === 'string';
};

const isSession = (value: unknown): value is ChatSession => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const session = value as Record<string, unknown>;
    return (
        typeof session.id === 'string' &&
        typeof session.title === 'string' &&
        typeof session.createdAt === 'number' &&
        typeof session.updatedAt === 'number' &&
        Array.isArray(session.messages) &&
        session.messages.every(isMessage)
    );
};

// Collapse whitespace and clip to a label-sized string for the session list.
export const truncateTitle = (text: string): string => {
    const clean = text.trim().replace(/\s+/g, ' ');
    return clean.length > TITLE_MAX ? `${clean.slice(0, TITLE_MAX).trimEnd()}…` : clean;
};

// Immutably append a message to one session, bumping updatedAt and — the first
// time a user message lands — deriving the session title from it. (The first
// message of a session is the discarded primer, so the first *user* bubble is
// genuinely the user's first question.)
export const upsertMessage = (
    sessions: ChatSession[],
    id: string,
    message: StoredMessage,
    titleIfEmpty?: string,
): ChatSession[] =>
    sessions.map(session => {
        if (session.id !== id) {
            return session;
        }
        const title =
            !session.title && message.role === 'user' && titleIfEmpty ? truncateTitle(titleIfEmpty) : session.title;
        return { ...session, title, updatedAt: Date.now(), messages: [...session.messages, message] };
    });

// Keep at most MAX_SESSIONS, dropping the oldest by updatedAt but never the
// active one. Under the cap the array is returned unchanged (order preserved).
export const capSessions = (sessions: ChatSession[], activeId: string | null): ChatSession[] => {
    if (sessions.length <= MAX_SESSIONS) {
        return sessions;
    }
    const byRecency = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt);
    const kept = byRecency.slice(0, MAX_SESSIONS);
    if (activeId && !kept.some(session => session.id === activeId)) {
        const active = sessions.find(session => session.id === activeId);
        if (active) {
            kept[kept.length - 1] = active;
        }
    }
    return kept;
};

// Server turns are a question and its saved reply; a reply this device has
// from the server carries its messageId.
const savedReplyCount = (messages: StoredMessage[]): number =>
    messages.filter(message => message.role === 'assistant' && message.messageId !== undefined).length;

// The device's copy wins; a chat the server has more of is marked to be fetched
// on open, and one the device never had is added with only the greeting.
export const mergeServerSessions = (
    sessions: ChatSession[],
    server: ChatServerSession[],
    greeting: string,
): ChatSession[] => {
    const byId = new Map(server.map(entry => [entry.session_id, entry]));
    const merged = sessions.map(session => {
        const entry = byId.get(session.id);
        if (!entry || Math.floor(entry.message_count / 2) <= savedReplyCount(session.messages)) {
            return session;
        }
        return {
            ...session,
            title: session.title || truncateTitle(entry.title),
            serverMessageCount: entry.message_count,
        };
    });
    const known = new Set(sessions.map(session => session.id));
    const added = server
        .filter(entry => !known.has(entry.session_id))
        .map(entry => ({
            id: entry.session_id,
            title: truncateTitle(entry.title),
            createdAt: Date.parse(entry.created_at) || Date.now(),
            updatedAt: Date.parse(entry.updated_at) || Date.now(),
            messages: [{ role: 'assistant' as const, content: greeting }],
            serverMessageCount: entry.message_count,
        }));
    return added.length === 0 && merged.every((session, index) => session === sessions[index])
        ? sessions
        : [...merged, ...added];
};

// Replace a chat's messages with the server's transcript, under the greeting.
export const applyServerTranscript = (
    sessions: ChatSession[],
    id: string,
    messages: ChatServerMessage[],
    greeting: string,
): ChatSession[] =>
    sessions.map(session => {
        if (session.id !== id) {
            return session;
        }
        const rest: ChatSession = { ...session };
        delete rest.serverMessageCount;
        const firstQuestion = messages.find(message => message.role === 'user')?.content ?? '';
        return {
            ...rest,
            title: session.title || truncateTitle(firstQuestion),
            messages: [
                { role: 'assistant', content: greeting },
                ...messages.map(
                    (message): StoredMessage => ({
                        role: message.role,
                        content: message.content,
                        ...(message.message_id !== undefined ? { messageId: message.message_id } : {}),
                        ...(message.rating === 1 || message.rating === -1 ? { rating: message.rating } : {}),
                    }),
                ),
            ],
        };
    });

export const loadStore = (uid: string): ChatStore => {
    ensureChatKeysRenamed();
    try {
        const raw = localStorage.getItem(storageKey(uid));
        if (!raw) {
            return emptyStore();
        }
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.sessions)) {
            return emptyStore();
        }
        const sessions = parsed.sessions.filter(isSession);
        const activeSessionId =
            typeof parsed.activeSessionId === 'string' &&
            sessions.some(session => session.id === parsed.activeSessionId)
                ? parsed.activeSessionId
                : sessions[0]?.id ?? null;
        return { activeSessionId, sessions };
    } catch {
        // Disabled storage, quota, or malformed JSON — start clean rather than throw.
        return emptyStore();
    }
};

export const saveStore = (uid: string, store: ChatStore): void => {
    const key = storageKey(uid);
    let sessions = capSessions(store.sessions, store.activeSessionId);
    // Retry loop for QuotaExceededError: shed the oldest non-active session and
    // try again until it fits or nothing more can be dropped, then give up
    // silently (the in-memory copy still works for this visit).
    for (;;) {
        try {
            localStorage.setItem(key, JSON.stringify({ activeSessionId: store.activeSessionId, sessions }));
            return;
        } catch {
            let oldestIndex = -1;
            let oldestAt = Infinity;
            sessions.forEach((session, index) => {
                if (session.id !== store.activeSessionId && session.updatedAt < oldestAt) {
                    oldestAt = session.updatedAt;
                    oldestIndex = index;
                }
            });
            if (oldestIndex === -1) {
                return;
            }
            sessions = sessions.filter((_, index) => index !== oldestIndex);
        }
    }
};

// ----------------------------------------------------------------------
// Consent — separate from the session data above on purpose: it's a one-time
// per-user decision, not a transcript. Recorded once, per uid, closed by
// default (the intro screen's checkbox starts unchecked; there is no implicit
// consent from merely opening the panel or clicking "New chat").
const CONSENT_KEY_PREFIX = 'cao_chat_consent:';

// Stored as the consent text's version, so a changed text is asked again.
export const hasGivenChatConsent = (uid: string, version: string): boolean => {
    ensureChatKeysRenamed();
    try {
        return localStorage.getItem(`${CONSENT_KEY_PREFIX}${uid}`) === version;
    } catch {
        return false;
    }
};

export const recordChatConsent = (uid: string, version: string): void => {
    try {
        localStorage.setItem(`${CONSENT_KEY_PREFIX}${uid}`, version);
    } catch {
        // ignore — storage disabled; consent will simply be asked again next time
    }
};

// The server asked for consent again (its record is missing or for an older
// text) — forget the local copy so the consent screen shows.
export const clearChatConsent = (uid: string): void => {
    try {
        localStorage.removeItem(`${CONSENT_KEY_PREFIX}${uid}`);
    } catch {
        // ignore — storage disabled
    }
};

// ----------------------------------------------------------------------
// The lessons the caller can ask about, kept only so the panel can show them
// before the server answers. Display data: what the chat may actually read is
// decided server-side on every turn, never from this copy.
const SCOPE_KEY_PREFIX = 'cao_chat_scope:';

export const loadChatScope = (key: string): ChatScopeLesson[] => {
    ensureChatKeysRenamed();
    try {
        const parsed = JSON.parse(localStorage.getItem(`${SCOPE_KEY_PREFIX}${key}`) ?? '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const saveChatScope = (key: string, lessons: ChatScopeLesson[]): void => {
    try {
        localStorage.setItem(`${SCOPE_KEY_PREFIX}${key}`, JSON.stringify(lessons));
    } catch {
        // ignore — storage full or disabled; the next prefetch fills it again
    }
};

// Module names in first-seen order — the server returns lessons grouped by module.
export const scopeModules = (lessons: ChatScopeLesson[]): string[] =>
    Array.from(new Set(lessons.map(lesson => lesson.module).filter(Boolean)));

export const setMessageRating = (
    sessions: ChatSession[],
    sessionId: string,
    messageId: number,
    rating: 1 | -1 | null,
): ChatSession[] =>
    sessions.map(session =>
        session.id !== sessionId
            ? session
            : {
                  ...session,
                  messages: session.messages.map(message =>
                      message.messageId === messageId ? { ...message, rating: rating ?? undefined } : message,
                  ),
              },
    );
